//* 관리자용 장례식장 정보 관리 페이지
import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { FuneralHomeInfoListStateSelector } from '@shared/api/funeralHomeInfo/funeralHomeInfo.selector';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import FuneralHomeInfoSearchList from '@shared/containers/FuneralHomeInfoSearcher/FuneralHomeInfoSearchList';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { Tag } from 'antd';
import FuzzySearch from 'fuzzy-search';
import { useNavigate } from 'react-router-dom';

import {
  ADMIN_FUNERAL_HOME_INFO_CREATE_MODAL_OPEN_ID,
  AdminFuneralHomeInfoCreateModal,
} from '../components/AdminFuneralHomeInfoCreateModal';
import { useAdminTitleHook } from '../hooks/useAdminTitleHook';
import {
  FuneralHomeDnStatus,
  useFuneralHomeDnStatusHook,
} from '../hooks/useFuneralHomeDnStatusHook';

type DnFilter = 'all' | 'none' | 'partial';

//* 전체 목록에서 미연결 장례식장을 앞으로 보내기 위한 정렬 순위
const DN_STATUS_ORDER: Record<FuneralHomeDnStatus, number> = {
  none: 0,
  partial: 1,
  full: 2,
};

const AdminFuneralHomeInfoManagePage: FC = function AdminFuneralHomeInfoManagePage() {
  useAdminTitleHook('장례식장정보 관리');
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [dnFilter, setDnFilter] = useState<DnFilter>('all');
  const funeralHomeInfoListState = useRecoilValue(FuneralHomeInfoListStateSelector);
  const navigate = useNavigate();
  const setCreateOpen = useSetRecoilState(
    simpleOpenAtomFamily(ADMIN_FUNERAL_HOME_INFO_CREATE_MODAL_OPEN_ID),
  );

  const {
    ready: dnReady,
    loading: dnLoading,
    error: dnError,
    getDnInfo,
    refetch: refetchDn,
  } = useFuneralHomeDnStatusHook();

  //* 전체 장례식장 기준 DN 미연결 집계 (검색어와 무관하게 총량을 보여줌)
  const { noneCount, partialCount, totalCount } = useMemo(() => {
    const list = funeralHomeInfoListState.data ?? [];
    if (!dnReady) {
      return { noneCount: 0, partialCount: 0, totalCount: list.length };
    }
    let none = 0;
    let partial = 0;
    list.forEach((item) => {
      const status = getDnInfo(item._id).status;
      if (status === 'none') none += 1;
      else if (status === 'partial') partial += 1;
    });
    return { noneCount: none, partialCount: partial, totalCount: list.length };
  }, [funeralHomeInfoListState.data, dnReady, getDnInfo]);

  const changeDnFilter = useCallback((filter: DnFilter) => {
    setDnFilter(filter);
    setSelectedPage(1);
  }, []);

  const renderDnTag = useCallback(
    (item: FuneralHomeInfo<undefined, string>) => {
      if (!dnReady) return null;
      const { status, connectedCount, brandCount } = getDnInfo(item._id);
      if (status === 'none') {
        return (
          <Tag color="red" className="m-0">
            DN 없음
          </Tag>
        );
      }
      if (status === 'partial') {
        return (
          <Tag color="orange" className="m-0">
            {`DN ${connectedCount}/${brandCount}`}
          </Tag>
        );
      }
      return null;
    },
    [dnReady, getDnInfo],
  );

  const funeralHomeInfoListTable = useMemo(() => {
    if (funeralHomeInfoListState.status === 'success' && funeralHomeInfoListState.data) {
      const funeralHomeInfoList = funeralHomeInfoListState.data;

      const searcher = new FuzzySearch(funeralHomeInfoList, [
        'name',
        'address',
        'phoneNumber',
      ]);

      const searchedFuneralHomeInfoList = searcher.search(searchValue);

      //* DN 현황을 아직 못 받았으면 기존 동작 그대로
      const dnFilteredList = !dnReady
        ? searchedFuneralHomeInfoList
        : dnFilter === 'all'
        ? //* 전체 보기에서는 미연결 → 일부연결 → 연결됨 순으로 앞에 띄움
          [...searchedFuneralHomeInfoList].sort(
            (a, b) =>
              DN_STATUS_ORDER[getDnInfo(a._id).status] -
              DN_STATUS_ORDER[getDnInfo(b._id).status],
          )
        : searchedFuneralHomeInfoList.filter(
            (item) => getDnInfo(item._id).status === dnFilter,
          );

      const onItemClickInListStep = (item: FuneralHomeInfo<undefined, string>) => {
        navigate(`/admin/funeral-home-info/${item._id}/edit`);
      };
      return (
        <FuneralHomeInfoSearchList
          searchedFuneralHomeInfoList={dnFilteredList}
          onItemClick={onItemClickInListStep}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          chunkSize={10}
          renderItemExtra={renderDnTag}
        />
      );
    } else {
      return (
        <div className="center-box">
          <Loading />
        </div>
      );
    }
  }, [
    funeralHomeInfoListState.data,
    funeralHomeInfoListState.status,
    navigate,
    searchValue,
    selectedPage,
    dnReady,
    dnFilter,
    getDnInfo,
    renderDnTag,
  ]);

  const filterButton = (filter: DnFilter, label: string, count: number) => {
    const selected = dnFilter === filter;
    return (
      <Button
        onClick={() => changeDnFilter(filter)}
        className={`rounded-full border px-3 py-1 text-13 ${
          selected
            ? 'border-blue-500 bg-blue-500 text-white'
            : 'border-gray-300 bg-white text-gray-700'
        }`}
      >
        {`${label} ${count}`}
      </Button>
    );
  };

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-end">
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-md px-3 py-1 text-14 theme-text-3 theme-bg-main"
        >
          신규 장례식장 생성
        </Button>
      </div>

      {/* //* DN(꽃집 연결) 미연결 장례식장은 부고에서 화환 주문 목록이 안 뜨므로 위에 노출 */}
      {dnReady && noneCount > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-13 text-red-700">
          <p className="font-bold">{`꽃집(DN) 미연결 장례식장 ${noneCount}곳`}</p>
          <p className="text-red-600">
            연결 전까지 이 장례식장 부고에서는 화환 주문 목록이 뜨지 않습니다. 장례식장을
            눌러 수정 페이지에서 꽃집을 연결하세요.
          </p>
        </div>
      )}
      {dnLoading && !dnReady && (
        <p className="text-13 text-gray-400">DN 연결현황 확인 중...</p>
      )}
      {dnError && (
        <div className="flex items-center gap-2 text-13 text-red-600">
          <span>DN 연결현황 조회 실패</span>
          <Button
            className="rounded border border-red-300 px-2 py-0.5 text-13"
            onClick={() => refetchDn()}
          >
            다시 시도
          </Button>
        </div>
      )}
      {dnReady && (
        <div className="flex flex-wrap items-center gap-2">
          {filterButton('all', '전체', totalCount)}
          {filterButton('none', 'DN 없음', noneCount)}
          {partialCount > 0 && filterButton('partial', '일부 미연결', partialCount)}
        </div>
      )}

      <TextField
        className="w-full"
        placeholder="장례식장 이름 및 지역을 검색하세요."
        onChange={(e) => {
          setSelectedPage(1);
          setSearchValue(e.target.value);
        }}
      />
      <div className="space-y-2 bg-gray-100">{funeralHomeInfoListTable}</div>
      <AdminFuneralHomeInfoCreateModal />
    </div>
  );
};

export default AdminFuneralHomeInfoManagePage;
